import LayerSetup from "./layerSetupFunctions.js";
import GeojsonFunctions from "./geojsonFunctions.js";
import * as turf from "@turf/turf";

const DEFAULT_PITCH_3D = 55;
const DEFAULT_PITCH_2D = 0;
const MAP_RETURN_TIMEOUT_MS = 30000; // 30 s

let maxPitch = 60;
let maxOffset = 0.005;
let mapReturnTimeoutId;
let adIndex = 0;

function createMap(initialMapSettings) {
  if (!initialMapSettings.mapContainerId) {
    console.warn("Invalid mapContainerId:", initialMapSettings.mapContainerId);
    return null;
  }
  mapboxgl.accessToken =
    initialMapSettings.accessToken ||
    "pk.eyJ1IjoidHJpbXJkIiwiYSI6ImNrdDJqcGlzbDBsM2Eyd3Vscm40djZ2cmoifQ.q6UwItGIss7wbXA-6oD5OA";
  return new mapboxgl.Map({
    container: initialMapSettings.mapContainerId,
    style: initialMapSettings.style || "mapbox://styles/trimrd/clxork3wz00lx01qrhqc652s3",
    zoom: initialMapSettings.zoom || 17,
    center: initialMapSettings.center || [13.6731417, 45.21163848], // [lng, lat]
    antialias: true, // create the gl context with MSAA antialiasing, so custom layers are antialiased
    pitch: initialMapSettings.is3d ? DEFAULT_PITCH_3D : DEFAULT_PITCH_2D,
    glyphs: "assets/Montserrat-SemiBold.pfb",
  });
}

async function initMap(campsaboutObject) {
  //Source and Layer setup:
  LayerSetup.setupFill(campsaboutObject);
  LayerSetup.setupPoi(campsaboutObject);
  LayerSetup.setupLine(campsaboutObject);
  LayerSetup.setupLabel(campsaboutObject);
  if (!campsaboutObject.is3d) {
    // 2D only setup:
    /* LayerSetup.setupLabel(campsaboutObject); */
  } else {
    // 3D only setup:
    LayerSetup.setupExtrusion(campsaboutObject);
    await setup3DUnits(campsaboutObject);
    setup3DBillboard(campsaboutObject);
    setup3DLabel(campsaboutObject);
    allowBufferDuringMove(campsaboutObject);
    campsaboutObject.map.on("zoom", () => {
      maxPitch = updatePitchLimit(campsaboutObject);
    });
    LayerSetup.setupLine(campsaboutObject);
  }

  //Other setup:

  //desni klik za preuzeti koordinate za POI
  campsaboutObject.map.on("contextmenu", (e) => {
    navigator.clipboard.writeText(JSON.stringify(e.lngLat.wrap()));
  });

  campsaboutObject.map.doubleClickZoom.disable();

  //automatsko vraćanje karte kad je predaleko nakon minute neaktivnosti
  //campsaboutObject.map.on("moveend", () => setupReCenterTimeout(campsaboutObject)); TODO find a way to pause it when in navigation
  //cursorOnClickables(campsaboutObject)
}

function cursorOnClickables (campsaboutObject) {
  campsaboutObject.map.on("mousemove", (event) => {
    const feature = getClicked3dObject(event) || getClickedGeojsonFeature(campsaboutObject.map, event);
    if(feature){
      if (feature.source === 'poi-source') {
        campsaboutObject.map.getCanvas().style.cursor = "pointer";
        return
      }
      if (feature.properties) {
        if(feature.properties.id){
          if(campsaboutObject.clickableIds.openModal[feature.properties.id] || campsaboutObject.clickableIds.openPopup[feature.properties.id]) {
            campsaboutObject.map.getCanvas().style.cursor = "pointer";
            return
          }
        }
      }
    }
    campsaboutObject.map.getCanvas().style.cursor = "";
  });
}

function createBoundBox(campsaboutObject, idFeatures = ["0"], layer = "fill") {
  if (!idFeatures || !idFeatures.length || !Array.isArray(idFeatures)) {
    console.warn("Illegal features", idFeatures);
    idFeatures = [];
  }

  const filteredGeoJson = campsaboutObject.sortedCampGeojson[layer].features.filter((f) =>
    idFeatures.includes(f.properties.id)
  );

  if (!filteredGeoJson.length) console.warn("No features match id's from geojson", idFeatures);

  let bbox = turf.bbox({
    type: "FeatureCollection",
    features: filteredGeoJson.length ? filteredGeoJson : [campsaboutObject.sortedCampGeojson[layer].features[0]],
  }); // [minX, minY, maxX, maxY]

  const margin = 0.1;
  const width = bbox[2] - bbox[0];
  const height = bbox[3] - bbox[1];

  bbox[0] -= width * margin; // minX
  bbox[1] -= height * margin; // minY
  bbox[2] += width * margin; // maxX
  bbox[3] += height * margin; // maxY

  return bbox;
}

function distanceFromMap(campsaboutObject) {
  if (!campsaboutObject) {
    console.warn("Invalid map:", campsaboutObject.map);
    return null;
  }

  const currentCenter = campsaboutObject.map.getCenter();

  const distanceToLeft = currentCenter.lng - campsaboutObject.bbox[0];
  const distanceToRight = campsaboutObject.bbox[2] - currentCenter.lng;
  const distanceToBottom = currentCenter.lat - campsaboutObject.bbox[1];
  const distanceToTop = campsaboutObject.bbox[3] - currentCenter.lat;

  return Math.max(
    Math.abs(distanceToLeft),
    Math.abs(distanceToRight),
    Math.abs(distanceToBottom),
    Math.abs(distanceToTop)
  );
}

function reCenterMap(campsaboutObject, skipAnimation = false, zoomOut = -2.5, ignoreZoomOut = true) {
  if (!campsaboutObject || !campsaboutObject.bbox) return;

  const bounds = [
    [campsaboutObject.bbox[0], campsaboutObject.bbox[1]], // southwest coordinates
    [campsaboutObject.bbox[2], campsaboutObject.bbox[3]], // northeast coordinates
  ];

  const fitOptions = {
    animate: !skipAnimation,
    pitch: campsaboutObject.is3d ? DEFAULT_PITCH_3D : DEFAULT_PITCH_2D,
    padding: 0, // Default padding if ignoring zoomOut
  };

  if (ignoreZoomOut) {
    // If ignoreZoomOut is true, use fitBounds with default padding (like the original function)
    campsaboutObject.map.fitBounds(bounds, fitOptions);
  } else {
    // Temporarily fit the bounds to get the calculated zoom level
    const calculatedZoom = campsaboutObject.map.fitBounds(bounds, { ...fitOptions, padding: 0 }).getZoom();

    // Adjust zoom level based on zoomOut parameter
    const targetZoom = calculatedZoom - zoomOut;

    // Get the center of the bounding box
    const center = [
      (bounds[0][0] + bounds[1][0]) / 2, // Longitude
      (bounds[0][1] + bounds[1][1]) / 2, // Latitude
    ];

    // Recenter and zoom to the target level
    campsaboutObject.map.setZoom(targetZoom);
    campsaboutObject.map.setCenter(center);
  }
}

function onClickAllLayers(campsaboutObject, clickHandler) {
  if (!campsaboutObject.map || typeof clickHandler != "function") return;
  campsaboutObject.map.on("click", (event) => {
    const feature = getClicked3dObject(event) || getClickedGeojsonFeature(campsaboutObject.map, event);
    if (feature) clickHandler(event, feature);
  });
}

function allowBufferDuringMove(campsaboutObject) {
  campsaboutObject.map.on("move", () => {
    if (campsaboutObject.map.getPitch() > maxPitch) {
      campsaboutObject.map.setPitch(maxPitch - 3); // Allow some buffer during move
    }
  });

  campsaboutObject.map.on("movestart", () => {
    if (campsaboutObject.map.getPitch() > maxPitch - 1) {
      campsaboutObject.map.setPitch(maxPitch - 5); // Enforce strict limit on move end
    }
  });

  campsaboutObject.map.on("moveend", () => {
    if (campsaboutObject.map.getPitch() > maxPitch - 1) {
      campsaboutObject.map.setPitch(maxPitch - 5); // Enforce strict limit on move end
    }
  });
}

function setupReCenterTimeout(campsaboutObject) {
  if (distanceFromMap(campsaboutObject) <= maxOffset) return;

  clearTimeout(mapReturnTimeoutId);

  mapReturnTimeoutId = setTimeout(() => {
    if (distanceFromMap(campsaboutObject) > maxOffset) {
      reCenterMap(campsaboutObject);
    }
  }, MAP_RETURN_TIMEOUT_MS);
}

async function openPopup(event, feature, campsaboutObject, $t, runOnPopUpClose, runOnNavigation, noGps) {
  const popup = new mapboxgl.Popup({ closeOnClick: true })
      .setLngLat(event.lngLat)
      .setHTML(buildPopupHtml(feature.properties, $t(feature.properties.icon), $t, noGps))
      .addTo(campsaboutObject.map);

  popup.on('close', () => {
    runOnPopUpClose()
  });

}

function buildPopupHtml(properties, defaultPopupText, $t, noGps) {
  //TODO request translations of wwwTekst and opis in cms? (default text is translated)
  const popupText = properties.wwwTekst && String(properties.wwwTekst) !== 'null' ? properties.wwwTekst : defaultPopupText

  const popupLinkHtml = properties.www && String(properties.www) !== 'null' ? `<a href="${properties.www}">${popupText}</a>` : popupText

  const popupImageHtml = properties.slika && String(properties.slika) !== 'null' ? `<br><img class="poiPopupImage" src="${properties.slika}" alt="Point of interest"/>` : ''

  const popupOpis = properties.opis && String(properties.opis) !== 'null' ? `<p>${properties.opis}</p>` : ''

  const navigateButton = noGps? '' : `
        <button id="navigateTo" class="btn btn-outline-secondary">
            <span>${$t('Navigation')}</span>
            <img src="https://campsabout.com/camp/polidor/1/assets/img/gps-arrow.svg">
        </button>`

  return `<h3>${popupLinkHtml}${popupImageHtml}</h3>${popupOpis}${navigateButton}`
}



export default {
  openPopup,
  createMap,
  initMap,
  createBoundBox,
  distanceFromMap,
  reCenterMap,
  onClickAllLayers,
  getEmptyGeojson: GeojsonFunctions.getEmptyGj,
  sortLayers: GeojsonFunctions.sortLayers,
  matchColorsWithIDs: GeojsonFunctions.matchColorsWithIDs,
  repaintMap: LayerSetup.repaintMap,
  setupFillFilter: LayerSetup.setupFillFilter
};
